import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useRef } from 'react';

import { Spinner, SPINNER_MOTION } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import {
  type RecorderResult,
  type ToolEvent,
  type ToolStatus,
} from '@/sections/asset/components/tabs/credentials/RecorderProgress/buildTimeline';
import { useRecorderOutcome } from '@/sections/asset/components/tabs/credentials/RecorderProgress/useRecorderOutcome';

// A caller-supplied step rendered at the TOP of the recorder's scrolling list,
// above the live recorder events. Used by the seed wizard to fold the two global
// "WebApplication(s) added" / "Credentials added" steps INTO the same scrolling
// table (so they share the recorder rows' check-circle styling). Omitting
// `leadingSteps` leaves the list byte-for-byte identical to today (Host A).
export interface RecorderLeadingStep {
  id: string;
  title: string;
  status: ToolStatus; // 'running' | 'success' | 'error'
}

interface RecorderProgressProps {
  conversationId: string;
  label: string;
  // Fired once when the agent's `finalize` tool response arrives. Caller is
  // responsible for showing the terminal state UI (Close button, retry, etc.).
  onComplete?: (result: RecorderResult) => void;
  // Optional rows pinned to the top of the scrolling list (before recorder
  // events). Defaults to none — Host A renders exactly as before.
  leadingSteps?: RecorderLeadingStep[];
}

export const RecorderProgress: React.FC<RecorderProgressProps> = ({
  conversationId,
  label,
  onComplete,
  leadingSteps,
}) => {
  const { events, finalize, result } = useRecorderOutcome(conversationId);

  // Fire onComplete exactly once when finalize lands.
  const completedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!finalize || !result || completedRef.current === finalize.id) return;
    completedRef.current = finalize.id;
    onComplete?.(result);
  }, [finalize, result, onComplete]);

  // Auto-scroll the inner list to the bottom as new events arrive so the
  // current step is always visible without expanding the modal.
  const listRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [events.length]);

  return (
    <div className="space-y-4">
      <Header label={label} hasResult={!!result} />
      {result && <ResultPanel label={label} result={result} />}
      <ol
        ref={listRef}
        className="border-chariot-stroke divide-chariot-stroke max-h-[40vh] divide-y overflow-y-auto rounded-lg border"
      >
        {leadingSteps?.map(step => (
          <LeadingStepRow key={step.id} step={step} />
        ))}
        {events.length === 0 ? (
          <li className="text-chariot-text-secondary flex items-center gap-2 p-3 text-sm">
            <Spinner />
            Waiting for the recorder to take its first step...
          </li>
        ) : (
          events.map(e => <ToolRow key={e.id} event={e} />)
        )}
      </ol>
    </div>
  );
};

const Header: React.FC<{ label: string; hasResult: boolean }> = ({
  label,
  hasResult,
}) => (
  <div>
    <h3 className="text-chariot-text-primary text-base font-semibold">
      Recording login for {label}
    </h3>
    {!hasResult && (
      <p className="text-chariot-text-secondary text-sm">
        The recorder is driving a browser through your login flow. Each step
        appears below as it runs.
      </p>
    )}
  </div>
);

// ResultPanel is the headline summary of what the recorder accomplished. It
// gets full breathing room (no truncation) so long rationales and blocker
// lists stay readable.
const ResultPanel: React.FC<{ label: string; result: RecorderResult }> = ({
  label,
  result,
}) => {
  const committed = !result.blocked;
  const accent = committed
    ? 'border-chariot-status-green/40 bg-chariot-status-green/5'
    : 'border-chariot-status-red/40 bg-chariot-status-red/5';
  const Icon = committed ? CheckCircleIcon : XCircleIcon;
  const iconColor = committed
    ? 'text-chariot-status-green'
    : 'text-chariot-status-red';
  const title = committed
    ? `Login recipe saved as "${result.label ?? label}"`
    : 'Recorder could not record this login';

  return (
    <div className={`flex gap-3 rounded-lg border p-4 ${accent}`}>
      <Icon className={`${iconColor} mt-0.5 size-6 shrink-0`} />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="text-chariot-text-primary text-sm font-semibold">
          {title}
        </div>
        {result.rationale && (
          <p className="text-chariot-text-secondary text-sm whitespace-pre-wrap">
            {result.rationale}
          </p>
        )}
        {!committed && result.blockers && result.blockers.length > 0 && (
          <div>
            <div className="text-chariot-text-primary text-xs font-semibold tracking-wide uppercase">
              Blockers
            </div>
            <ul className="text-chariot-text-secondary mt-1 list-disc space-y-1 pl-5 text-sm">
              {result.blockers.map((b, i) => (
                <li key={i} className="whitespace-pre-wrap">
                  {b}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// A caller-supplied leading row. Shares ToolRow's exact row chrome (the
// `<li className="flex items-start gap-3 p-3">` + StatusIcon) so a global step
// and a recorder step are visually indistinguishable. Title only — no
// description/error sub-lines.
const LeadingStepRow: React.FC<{ step: RecorderLeadingStep }> = ({ step }) => (
  <li className="flex items-start gap-3 p-3">
    <StatusIcon status={step.status} />
    <div className="min-w-0 flex-1">
      <div className="text-chariot-text-primary truncate text-sm font-medium">
        {step.title}
      </div>
    </div>
  </li>
);

const ToolRow: React.FC<{ event: ToolEvent }> = ({ event }) => {
  const isTerminal = event.name === 'finalize' && event.status !== 'running';
  return (
    <li className="flex items-start gap-3 p-3">
      <StatusIcon status={event.status} />
      <div className="min-w-0 flex-1">
        <div className="text-chariot-text-primary truncate text-sm font-medium">
          {isTerminal ? 'Recorder finalized' : event.prettyName}
        </div>
        {event.description && !isTerminal && (
          <div className="text-chariot-text-secondary truncate font-mono text-xs">
            {event.description}
          </div>
        )}
        {event.errorText && (
          <div className="text-chariot-status-red mt-1 text-xs">
            {event.errorText}
          </div>
        )}
      </div>
    </li>
  );
};

const StatusIcon: React.FC<{ status: ToolEvent['status'] }> = ({ status }) => {
  switch (status) {
    case 'running':
      return (
        <ArrowPathIcon
          className={cn(
            'text-chariot-brand-primary size-5 shrink-0',
            SPINNER_MOTION
          )}
          role="status"
          aria-label="In progress"
        />
      );
    case 'success':
      return (
        <CheckCircleIcon className="text-chariot-status-green size-5 shrink-0" />
      );
    case 'error':
      return (
        <XCircleIcon className="text-chariot-status-red size-5 shrink-0" />
      );
    default:
      return (
        <ExclamationTriangleIcon className="text-chariot-text-secondary size-5 shrink-0" />
      );
  }
};
